import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { Col, Form, Label, Row } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import ButtonWithLoader from '../../../core/components/ButtonWithLoader';
import { requiredInput as RequiredInput } from '../../../core/formValidation';
import { getApiStatus } from '../../../core/utils';
import '../../../styles/login-form.scss';
import { login as loginAction, statusReset } from '../actions';
import { statusSelector } from '../../../store/authReducer';
import LoginHeader from './LandingPageHeader';
import LandingPageFooter from './LandingPageFooter';
import LoginSlideshow from './LoginSlideshow';

type FormValues = { username: string; password: string };

const LoginForm = (): JSX.Element => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const statusMessage = useSelector((state) => statusSelector(state));
  const isLoading = useSelector((state) => getApiStatus(state, 'LOGIN')) as boolean;

  const { control, handleSubmit, formState } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { username: '', password: '' },
  });

  const onSubmit = (values: FormValues) => {
    dispatch(loginAction({ username: values.username, password: values.password }));
  };

  const formDisabled = !formState.isValid || formState.isSubmitting;

  const handleForgotPasswordButton = () => {
    dispatch(statusReset());
    history.push('/forgot-password');
  };
  return (
    <div className="login-container">
      <LoginSlideshow />
      <Form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        className="login-form"
        data-testid="login-form"
      >
        <LoginHeader />
        <Row>
          <Col className="login-input-remove-padding">
            <Label for="username" className="login-input-label">
              {intl.formatMessage({ id: 'emailAddress' })}
            </Label>
            <Controller
              name="username"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <RequiredInput
                  input={{
                    ...field,
                    id: 'username',
                    type: 'text',
                    className: 'login-form-input',
                    placeholder: intl.formatMessage({ id: 'emailAddress' }),
                  }}
                />
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col className="login-input-remove-padding">
            <Label for="password" className="login-input-label">
              {intl.formatMessage({ id: 'password' })}
            </Label>
            <Controller
              name="password"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <RequiredInput
                  input={{
                    ...field,
                    id: 'password',
                    type: 'password',
                    className: 'login-form-input',
                    placeholder: intl.formatMessage({ id: 'password' }),
                  }}
                />
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col className="login-input-remove-padding">
            <ButtonWithLoader
              handleClick={handleSubmit(onSubmit)}
              disabled={formDisabled}
              isLoading={isLoading || formState.isSubmitting}
            >
              <div className="login-button-text">{intl.formatMessage({ id: 'login' })}</div>
            </ButtonWithLoader>
            {statusMessage && statusMessage.type === 'failure' && (
              <div data-testid="error-alert" className="login-error alert alert-danger">
                {statusMessage.message}
              </div>
            )}
            <div className="forgot-password-back" onClick={handleForgotPasswordButton}>
              {intl.formatMessage({ id: 'forgotPassword' })}{' '}
            </div>
            <LandingPageFooter />
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default LoginForm;
